<template>
  <v-dialog v-model="internalValue" max-width="600" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Gift Subscription</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="device" class="mb-4">
          <div class="text-subtitle-1 font-weight-medium">
            {{ device.customName || 'Unnamed Device' }}
          </div>
          <div class="text-caption grey--text">
            ID: {{ device.id }} | Company: {{ device.companyName }}
          </div>
          <div class="text-caption grey--text">
            Current Status:
            <v-chip :color="getStatusColor(device.status)" text-color="white" x-small class="ml-1">
              {{ device.status }}
            </v-chip>
          </div>
          <div v-if="device && device.validTo" class="text-caption grey--text">
            Current Expiry: {{ formatDate(device.validTo) }}
          </div>
        </div>

        <v-form ref="form" v-model="formValid" @submit.prevent="giftSubscription">
          <!-- Gift Months Selection -->
          <v-select
            v-model="form.giftMonths"
            :items="monthOptions"
            label="Gift Duration *"
            required
            :rules="[(v) => !!v || 'Gift duration is required']"
            hint="Select how many months to add to the subscription"
            persistent-hint
            class="mb-4"
          />

          <!-- Email Options -->
          <v-checkbox
            v-model="form.sendEmail"
            label="Send notification email to device users"
            hint="Notify users about the gift subscription via email"
            persistent-hint
            class="mb-4"
          />

          <!-- Email Template Selection (when sending email) -->
          <v-select
            v-if="form.sendEmail"
            v-model="form.emailTemplate"
            :items="emailTemplateOptions"
            label="Email Template"
            hint="Select the email template to use for notifications"
            persistent-hint
            class="mb-4"
          />

          <!-- Gift Information -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="text-subtitle-2">How gift subscriptions work</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="text-body-2">
                  <li>
                    If the device has an active subscription, the gift months will be added to the
                    current expiry date
                  </li>
                  <li>
                    If the device has no active subscription or it's expired, a new subscription
                    will be created
                  </li>
                  <li>Gift subscriptions are free and don't require payment</li>
                  <li>Users will receive an email notification about the gift (if enabled)</li>
                  <li>
                    The subscription will be immediately active and visible in the user's account
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close"> Cancel </v-btn>
        <v-btn color="success" :loading="loading" :disabled="!formValid" @click="giftSubscription">
          <v-icon left>mdi-gift</v-icon>
          Gift {{ form.giftMonths }} Month{{ form.giftMonths === 1 ? '' : 's' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { adminGiftSubscription } from '@/graphql/mutations/adminGiftSubscription'

export default {
  name: 'GiftSubscriptionModal',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    device: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      formValid: false,
      loading: false,
      form: {
        giftMonths: 1,
        sendEmail: true,
        emailTemplate: 'subscription-gift-cs2',
      },

      monthOptions: [
        { text: '1 Month', value: 1 },
        { text: '2 Months', value: 2 },
        { text: '3 Months', value: 3 },
        { text: '4 Months', value: 4 },
        { text: '5 Months', value: 5 },
        { text: '6 Months', value: 6 },
        { text: '7 Months', value: 7 },
        { text: '8 Months', value: 8 },
        { text: '9 Months', value: 9 },
        { text: '10 Months', value: 10 },
        { text: '11 Months', value: 11 },
        { text: '12 Months', value: 12 },
      ],

      emailTemplateOptions: [
        { text: 'Gift Subscription (Czech)', value: 'subscription-gift-cs2' },
        { text: 'Gift Subscription (English)', value: 'subscription-gift-en2' },
        { text: 'Gift Subscription (German)', value: 'subscription-gift-de2' },
        { text: 'Gift Subscription (Polish)', value: 'subscription-gift-pl2' },
      ],
    }
  },

  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm()
      }
    },
  },

  methods: {
    close() {
      this.internalValue = false
    },

    resetForm() {
      this.form = {
        giftMonths: 1,
        sendEmail: true,
        emailTemplate: 'subscription-gift-cs2',
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    getStatusColor(status) {
      switch (status) {
        case 'VALID':
          return 'success'
        case 'SOON_EXPIRING':
          return 'warning'
        case 'EXPIRED':
          return 'error'
        case 'NOT_DEFINED':
          return 'grey'
        default:
          return 'grey'
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },

    calculateNewExpiry() {
      if (!this.form.giftMonths || !this.device) return 'N/A'

      let baseDate
      if (this.device.validTo && new Date(this.device.validTo) > new Date()) {
        // Extend existing subscription
        baseDate = new Date(this.device.validTo)
      } else {
        // Create new subscription from today
        baseDate = new Date()
      }

      const newDate = new Date(baseDate)
      newDate.setMonth(newDate.getMonth() + this.form.giftMonths)

      return newDate.toLocaleDateString()
    },

    async giftSubscription() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true

      try {
        const input = {
          deviceId: this.device.id,
          giftMonths: this.form.giftMonths,
          sendEmail: this.form.sendEmail,
        }

        if (this.form.sendEmail && this.form.emailTemplate) {
          input.emailTemplate = this.form.emailTemplate
        }

        const { data } = await this.$apollo.mutate({
          mutation: adminGiftSubscription,
          variables: { input },
        })

        console.log('Gift subscription response data:', data)
        console.log(
          'Checking success condition:',
          data.adminGiftSubscription && data.adminGiftSubscription.success,
        )

        if (data.adminGiftSubscription && data.adminGiftSubscription.success) {
          const result = data.adminGiftSubscription
          let message = `Successfully gifted ${result.giftMonths} month${
            result.giftMonths === 1 ? '' : 's'
          } to ${result.deviceName || 'device'}!`

          if (result.emailsSent > 0) {
            message += ` Notification emails sent to ${result.emailsSent} user${
              result.emailsSent === 1 ? '' : 's'
            }.`
          }

          console.log('Showing success message:', message)

          // Reset loading first, then show success and close
          this.loading = false

          this.$toast.success(message)
          console.log('Emitting success event')
          this.$emit('success')
        } else {
          console.log('Gift subscription failed, data:', data)
          this.$toast.error('Failed to gift subscription')
        }
      } catch (error) {
        console.error('Error gifting subscription:', error)
        this.$toast.error('Error gifting subscription: ' + error.message)
      } finally {
        console.log('Gift subscription finally block - setting loading to false (backup)')
        if (this.loading) {
          this.loading = false
        }
      }
    },
  },
}
</script>

<style scoped></style>
